import React from 'react';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Box, Button, Typography } from '@mui/material';

import { OfferCategory, Reservation, ShippingStatus } from '../graphql/API';
import { ReservationPost } from '../pages/ReservationDetail/ReservationDetail';
import { extractSubscriptionPlan } from '../pages/SubscriptionPlans/common/helper';
import { useAppSelector } from '../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../utils/venue-tags';
import LoadingButton from './Button/LoadingButton';

type Props = {
  reservation: Reservation;
  detailPage?: boolean;
};

export const ReservationPostLink: React.FC<Props> = ({ reservation, detailPage = false }) => {
  const navigate = useNavigate();
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const venueState = useAppSelector((state) => state.venue.value);
  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const handleUpgrade = () => {
    navigate('/plans');
  };

  const postLinkHandle = React.useCallback(() => {
    window.open(reservation?.postLink || '', '_blank');
  }, [reservation?.postLink]);

  return (
    <>
      {(venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans) &&
        (subscriptionPlan === 'starter' || subscriptionPlan === 'plus')) ||
        (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
          (subscriptionPlan === 'basic' || 'starter' || subscriptionPlan === 'plus')) ? (
        <Box display={'flex'} alignItems={'center'} gap={'6px'}>
          {!reservation.postOnInstagramStories &&
            !reservation.postOnInstagramStories2x &&
            !reservation.postOnInstagramStories3x &&
            !reservation.postOnInstagramReels &&
            !reservation.postOnTikTokVideo &&
            !reservation.postOnInstagramFeed ? (
            <Typography sx={{ color: '#848484', fontSize: '15px', fontWeight: 500 }}>
              No requirements
            </Typography>
          ) : (
            <>
              {!detailPage && (
                <>
                  {reservation.offerCategory !== OfferCategory.product ? (
                    <>
                      {reservation.validation === ReservationPost.validationFailed ? (
                        <CloseIcon htmlColor='#DC5564' />
                      ) : (
                        reservation.postLink ? '' : '-'
                      )}
                    </>
                  ) : (
                    <>
                      {reservation.requirementsSubmitted &&
                        reservation.shippingStatus === ShippingStatus.shipped &&
                        reservation.postLink ? (
                        <DoneIcon htmlColor='#37BB90' />
                      ) : reservation.requirementsSubmitted === false &&
                        !reservation.postLink &&
                        reservation.shippingStatus === ShippingStatus.shipped ? (
                        <CloseIcon htmlColor='#DC5564' />
                      ) : (
                        '-'
                      )}
                    </>
                  )}
                </>
              )}
              {reservation.postLink && detailPage ? (
                <Button
                  size='medium'
                  variant='outlined'
                  sx={{ borderRadius: '7px', px: '18px' }}
                  onClick={postLinkHandle}
                >
                  Go to post
                </Button>
              ) : reservation.postLink ? (
                <LoadingButton
                  variant='text'
                  sx={{ textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(reservation.postLink || '', '_blank');
                  }}
                >
                  See post
                </LoadingButton>
              ) : null}
            </>
          )}
        </Box>
      ) : (
        <Box display={'flex'} flexDirection={'column'} gap={'6px'} alignItems={'flex-start'}>
          <Typography sx={{ color: '#848484', fontSize: '15px', fontWeight: 500 }}>
            Not included in your plan
          </Typography>
          <button
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              fontWeight: 500,
              padding: 0,
              margin: 0,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleUpgrade();
            }}
          >
            Upgrade
          </button>
        </Box>
      )}
    </>
  );
};
